import { useEffect, useReducer } from "react";
import { useTolgee } from "@tolgee/react";
import { Locale } from "date-fns";
import { enGB } from "date-fns/locale";
import { ENGLISH_LANG_CODE } from "@evennt-tech/api-v2-core";
import loadDateFnsLocale from "tools/date/loadDateFnsLocale";

// Locales are loaded asynchronously; keeping the loaded ones lets a component
// mounted later (a modal, a drawer) format in the right locale on its first
// render instead of briefly falling back to English.
const loadedLocales = new Map<string, Locale>([[ENGLISH_LANG_CODE, enGB]]);

const useDateFnsLocale = (): Locale => {
  const tolgee = useTolgee(["language"]);
  const code = tolgee.getLanguage() ?? ENGLISH_LANG_CODE;
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (loadedLocales.has(code)) return undefined;
    let active = true;
    loadDateFnsLocale(code).then((resolved) => {
      // `enGB` for another language means the load failed; leave it uncached
      // so the next mount retries.
      if (resolved === enGB) return;
      loadedLocales.set(code, resolved);
      if (active) rerender();
    });

    return () => {
      active = false;
    };
  }, [code]);

  return loadedLocales.get(code) ?? enGB;
};

export default useDateFnsLocale;
